/* global process */
import { useLocation, Routes, Route } from 'react-router-dom';
import { useEffect } from "react";
import { useDarkModeStore } from "@/store/darkModeStore";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AccessibilityProvider } from "@/providers/AccessibilityProvider";
import { PreferencesProvider } from "@/providers/PreferencesProvider";
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import '@/index.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Hero } from '@/components/Hero';
import { Features } from '@/components/Features';
import { Testimonials } from '@/components/Testimonials';
import SettingsPanel from '@/components/SettingsPanel';
import { useSettingsStore } from '@/store/settingsStore';
import { useAccessibilityStore } from '@/store/accessibilityStore';
import { LanguageProvider } from '@/providers/LanguageProvider';
import { DarkModeProvider } from '@/providers/DarkModeProvider';
import DonationWidget from '@/components/DonationWidget';
import HomePage from '@/screens/Home';
import Match from '@/screens/Match';
import Results from '@/screens/Results';
import Details from '@/screens/Details';
import AppealLetter from '@/screens/AppealLetter';
import { ProgramSearch } from '@/components/ProgramSearch';
import FloatingActions from '@/components/FloatingActions';
import OnboardingFlow from "@/components/OnboardingFlow";
import { useOnboardingState } from "@/store/userPreferencesStore";
import { AidGroupsShowcase } from '@/components/AidGroupsShowcase';
import { FAQ } from '@/components/FAQ';
import { useLanguageChangeListener } from '@/hooks/useLanguageChangeListener';

function AppContent() {
  const location = useLocation();
  const { isDarkMode } = useDarkModeStore();
  const { isSettingsOpen, toggleSettings } = useSettingsStore();
  const { highContrast } = useAccessibilityStore();
  const { onboarding, updateOnboarding } = useOnboardingState();

  // Listen for language changes and regenerate AI content
  useLanguageChangeListener();

  // Apply theme class on mount and when theme changes
  useEffect(() => {
    // Remove any existing theme classes
    document.documentElement.classList.remove("dark", "light");
    
    // Apply the current theme class
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.add("light");
    }
  }, [isDarkMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  useEffect(() => {
    // Apply theme class
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.classList.toggle('high-contrast', highContrast);
  }, [isDarkMode, highContrast]);

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <PreferencesProvider>
          <AccessibilityProvider>
            <LanguageProvider>
              <DarkModeProvider>
                <div className="relative min-h-screen bg-background font-sans antialiased">
                  <Header />
                  
                  <Routes>
                    <Route path="/" element={
                      <main>
                        <Hero />
                        <Features />
                        <AidGroupsShowcase />
                        <Testimonials />
                        <FAQ />
                        <DonationWidget 
                          title="Support Our Mission"
                          desc="Your donation helps us provide emergency aid and support to those in need. Every contribution makes a difference."
                          donate="Donate with Algorand"
                          powered="Powered by Algorand Blockchain"
                        />
                      </main>
                    } />
                    <Route path="/start-here" element={<HomePage />} />
                    <Route path="/match" element={<Match />} />
                    <Route path="/results" element={<Results />} />
                    <Route path="/details" element={<Details />} />
                    <Route path="/appeal-letter" element={<AppealLetter />} />
                    <Route path="/search-programs" element={<ProgramSearch />} />
                  </Routes>

                  <Footer />
                  <FloatingActions />

                  <AnimatePresence>
                    {isSettingsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                      >
                        <div className="relative w-full max-w-2xl p-6 bg-background rounded-lg shadow-xl">
                          <button
                            className="absolute top-4 right-4 text-foreground hover:text-primary"
                            onClick={toggleSettings}
                          />
                          <SettingsPanel isOpen={isSettingsOpen} onClose={toggleSettings} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>
                    {!onboarding.completed && (
                      <OnboardingFlow
                        onComplete={() => updateOnboarding({ completed: true })}
                      />
                    )}
                  </AnimatePresence>
                </div>
              </DarkModeProvider>
            </LanguageProvider>
          </AccessibilityProvider>
        </PreferencesProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
}

export default AppContent;